import * as Dtos from "@/api/dtos";
import { AppSettings } from "@/AppSettings";
import { crfReadOnly } from "@/utils/CrfFormUtils";
import ScreeningDialogContext from "@/contexts/ScreeningDialogContext";
import { Box, Button, ButtonGroup, Dialog, DialogActions, DialogContentText, styled, Typography } from "@mui/material";
import { ConditionMode, useFormActions, useScopedField, ValidateOn } from '@ngt/forms-core';
import { CollaboratingGroupContext, CrfCondition, CrfForm, CrfFormByCodesProvider, DatePicker, FormBreadcrumbs, FormDefinitionContext, FormSubmitButton, GroupedField, ICrfConditionParameters, ICrfFormProps, InstitutionByCodeProvider, InstitutionContext, InstitutionsContext, MasterGroupContext, PageLayout, Password, PatientContext, PatientDetailsByStudyNumberProvider, PatientsContext, RouteLoading, Select, Text } from "@ngt/opms";
import { Permission, usePermissionsByIds } from "@ngt/opms-bctapi";
import { FC, useCallback, useContext, useEffect, useMemo } from "react";
import GroupedExpandableField from "./fields/GroupedExpandableField";

const StyledDialogContentText = styled(DialogContentText)(({ theme }) => ({
    padding: theme.spacing(0,2,2)
}));

const StyledDialogTitle = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(2),
    fontSize: '1.25rem',
    fontWeight: 500
}));

const screenFail = ({ formState }: ICrfConditionParameters<Dtos.ScreeningForm>) => formState?.value?.outcome === Dtos.LookupScreeningOutcomeType.Fail;

interface IScreeningFormProps {
    refreshScreeningForms?: () => Promise<Dtos.ScreeningForm[] | undefined>;
}

type OmitCalculatedTypes<T> = Omit<T, 'onValidate' | 'allowSubmit' | 'initialValues' | 'lookups' | 'labels'>

export interface ICrfDialogFormProps<Type extends Dtos.IForm> extends OmitCalculatedTypes<ICrfFormProps<Type>> {
    dialogTitle?: string;
    hideButtons?: boolean;
    hideSubmit?: boolean;
    hideCancel?: boolean;
}


const CrfDialogForm = <Type extends Dtos.IForm>({
    dialogTitle,
    children,
    ...formProps
}: ICrfDialogFormProps<Type>) => {
    return (
        <CrfForm
            {...formProps}
            debug={AppSettings.debug}
            disableHeading
        >
            <StyledDialogTitle
                color="secondary"
            >
                {dialogTitle ?? formProps.entity}
            </StyledDialogTitle>
            
            {children}
        </CrfForm>
    );
}

const ScreenOutcomeFields: FC = () => {
    const { screeningOutcome } = useContext(ScreeningDialogContext);

    const { setFieldValue } = useFormActions();

    useEffect(() => {
        if(screeningOutcome) {
            setFieldValue(`outcome`, screeningOutcome, true, true, true, false);
        }
    }, [screeningOutcome, setFieldValue]);

    const { state: { value: outcomeValue } } = useScopedField(`outcome`, { value: true });

    return (
        <>
            {
                (screeningOutcome ?? outcomeValue) === Dtos.LookupScreeningOutcomeType.Proceed && (
                    <>
                        <StyledDialogContentText>
                            Are you sure you want to confirm a successful screening and move the patient to Consent?
                        </StyledDialogContentText>
                        {
                            /**
                             * The email is currently captured when the participant is first added according to the current specifications.
                             * I have a feeling that they will probably want this to be done here instead, but for now I shall adhere
                             * to the specs documents provided
                             */
                        }
                        {/*
                            <StyledDialogContentText>
                                Proceeding to consent will send an email notification to the participant. This email contains the link to the information page.
                            </StyledDialogContentText>
                            <GroupedField
                                propertyPath="participantEmail"
                                component={Text}
                                FieldGridProps={{
                                    size: {
                                        xl: 12,
                                        lg: 12,
                                        md: 12,
                                        sm: 12,
                                        xs: 12
                                    }
                                }}
                                GroupedFieldErrorFlagProps={{
                                    showErrorFlag: (errors, touched, _focused, _validating, isAlwaysHidden, _isAlwaysShown) => {
                                        if (isAlwaysHidden) {
                                            return false;
                                        }

                                        // if (isAlwaysShown) {
                                        //     return true;
                                        // }

                                        if (touched && errors && errors.length > 0) {
                                            return true;
                                        }
                                        
                                        return false;
                                    }
                                }}
                                GroupedFieldErrorDisplayProps={{
                                    showError: (name, _error, touched, _errors, _validating, _isAlwaysShown) => {
                                        return touched[name] ? true : false;
                                    }
                                }}
                            /> 
                        */}
                    </>
                )
            }
            {
                (screeningOutcome ?? outcomeValue) === Dtos.LookupScreeningOutcomeType.Fail && (
                    <GroupedField
                        propertyPath="screenFailReason"
                        component={Select}
                        LabelProps={{
                            label: screeningOutcome ? <>Please provide the reason for failing the screening</> : undefined
                        }}
                        FieldGridProps={{
                            size: {
                                xl: 12,
                                lg: 12,
                                md: 12,
                                sm: 12,
                                xs: 12
                            }
                        }}
                        GroupedFieldErrorFlagProps={{
                            showErrorFlag: (errors, touched, _focused, _validating, isAlwaysHidden, _isAlwaysShown) => {
                                if (isAlwaysHidden) {
                                    return false;
                                }
        
                                // if (isAlwaysShown) {
                                //     return true;
                                // }
        
                                if (touched && errors && errors.length > 0) {
                                    return true;
                                }
                                
                                return false;
                            }
                        }}
                        GroupedFieldErrorDisplayProps={{
                            showError: (name, _error, touched, _errors, _validating, _isAlwaysShown) => {
                                return touched[name] ? true : false;
                            }
                        }}
                    />
                )
            }
        </>
    );
}

const ScreeningCrfForm: FC = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf, 
            canAdministerOpms
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate,
            Permission.OpmsAdminister
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return <>
        <CrfForm
            validateOn={ValidateOn.onChange}
            readOnly={crfReadOnly(canUpdateCrf)}
            debug={AppSettings?.debug}
        >
            {
                patient?.patientStateId != Dtos.PatientStateType.ScreeningFailed && <>
                    <GroupedField
                        propertyPath="initials"
                        component={(canAdministerOpms && patient?.id) ? Password : Text}
                        disabled={!!canAdministerOpms && !!patient?.id}
                    />
                    <GroupedField
                        propertyPath="dob"
                        component={(canAdministerOpms && patient?.id) ? Password : DatePicker}
                        disabled={!!canAdministerOpms && !!patient?.id}
                    />
                    <GroupedField
                        propertyPath="email"
                        component={(canAdministerOpms && patient?.id) ? Password : Text}
                        disabled={!!canAdministerOpms && !!patient?.id}
                    />
                </>
            }
            <CrfCondition
                mode={ConditionMode.Show}
                condition={screenFail}
                subscription={{ value: true }}
            >
                <GroupedField
                    propertyPath="screenFailReason"
                    component={Select}
                />
            </CrfCondition>
        </CrfForm>
    </>
}

const ScreeningDialogForm: FC<IScreeningFormProps> = ({ refreshScreeningForms }) => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institutions } = useContext(InstitutionsContext);
    const { data: patients, refresh: refreshPatients } = useContext(PatientsContext);
    const { dialogOpen, patientStudyNumber, institutionCode, screeningOutcome, setDialogOpen, setPatientStudyNumber, setScreeningOutcome } = useContext(ScreeningDialogContext);

    const institution = useMemo(() => {
        return institutions?.find(x => x.code === institutionCode)
    }, [institutionCode, institutions])

    const patient = useMemo(() => {
        return patients?.find(p => p.studyNumber === patientStudyNumber) as Dtos.Patient;
    }, [patients, patientStudyNumber]);

    const {
        permissions: [
            canUpdateCrf, 
            canAdministerOpms
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate,
            Permission.OpmsAdminister
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const onDialogClose = useCallback(() => {
        setDialogOpen(false);
        setPatientStudyNumber(null);
        setScreeningOutcome(null);
    }, [setDialogOpen, setPatientStudyNumber, setScreeningOutcome]);

    // const onFormSubmit = useCrfFormSubmit<Dtos.ScreeningForm>();

    const afterDialogSave = useCallback(() => {
        refreshPatients();
        refreshScreeningForms?.();
        onDialogClose();

        // if(screeningOutcome === Dtos.LookupScreeningOutcomeType.Proceed)
        // {
        //     setConsentDialogPatientStudyNumber(patient?.studyNumber);
        // }

    }, [onDialogClose, refreshPatients, refreshScreeningForms, screeningOutcome, patient]);

    const dialogTitle = useMemo(() => {
        return `${screeningOutcome ? screeningOutcome === Dtos.LookupScreeningOutcomeType.Proceed ? 'Successful' : 'Failed' : ''} 
            Screening ${patient ? '(Participant ' + patient.studyNumber + ')' : ''}`

    }, [ patient, screeningOutcome]);

    const showScreeningFields = !screeningOutcome && patient?.patientStateId != Dtos.PatientStateType.ScreeningFailed;

    const form = (
        <CrfFormByCodesProvider
            institutionCode={patient?.institutionCode ?? institutionCode ?? undefined}
            patientStudyNumber={patient?.studyNumber}
            eventDefinitionIdentifier="screening"
            eventRepeat={1}
            formDefinitionIdentifier="screening"
            formRepeat={1}
            createEvent={true}
            createPatient={true}
            createForm={true}
            loadBeforeMount
            LoadingComponent={RouteLoading}
        >
            <CrfDialogForm
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                hideButtons={!!screeningOutcome}
                hideSubmitAndReturn={true}
                onCancel={onDialogClose}
                afterFormSave={afterDialogSave}
                dialogTitle={dialogTitle}
            >
                {
                    showScreeningFields && <>
                        <GroupedField
                            propertyPath="initials"
                            component={(canAdministerOpms && patientStudyNumber) ? Password : Text}
                            FieldGridProps={{
                                size: {
                                    xl: 6,
                                    lg: 6,
                                    md: 6,
                                    sm: 6,
                                    xs: 12
                                }
                            }}
                            disabled={!!canAdministerOpms && !!patientStudyNumber}
                        />
                        <GroupedField
                            propertyPath="dob"
                            component={(canAdministerOpms && patientStudyNumber) ? Password : DatePicker}
                            FieldGridProps={{
                                size: {
                                    xl: 6,
                                    lg: 6,
                                    md: 6,
                                    sm: 6,
                                    xs: 12
                                }
                            }}
                            disabled={!!canAdministerOpms && !!patientStudyNumber}
                        />
                        <GroupedExpandableField
                            propertyPath="email"
                            component={(canAdministerOpms && patientStudyNumber) ? Password : Text}
                            FieldGridProps={{
                                size: {
                                    xl: 6,
                                    lg: 6,
                                    md: 6,
                                    sm: 6,
                                    xs: 12
                                }
                            }}
                            disabled={!!canAdministerOpms && !!patientStudyNumber}
                            expandableContent={
                            <Box>
                                <Typography>
                                    Providing email will send participant link to information about the trial
                                </Typography>
                            </Box>
                        }
                        />
                    </>
                }
                {
                    <ScreenOutcomeFields 

                    />
                }
                {
                    screeningOutcome && <>
                        <DialogActions>
                            <ButtonGroup
                                sx={{
                                    display: "flex",
                                    gap: 1
                                }}
                            >
                                <Button
                                    variant="text"
                                    color="secondary"
                                    onClick={onDialogClose}
                                >
                                    Cancel
                                </Button>
                                <FormSubmitButton
                                    variant="contained"
                                    color="primary"
                                >
                                    Confirm
                                </FormSubmitButton>
                            </ButtonGroup>
                        </DialogActions>
                    </>
                }
            </CrfDialogForm>
        </CrfFormByCodesProvider>
    )


    return (
        <>
            {
                (patient || institutionCode) && (
                    <Dialog
                        open={dialogOpen}
                        onClose={onDialogClose}
                        aria-labelledby="form-dialog-title"
                        fullWidth={true}
                        scroll="body"
                        maxWidth="sm"
                    >
                        {
                            (permissionsLoading) ?  
                                <RouteLoading /> :
                                patient ?
                                    <PatientDetailsByStudyNumberProvider
                                        patientStudyNumber={patient?.studyNumber}
                                        loadBeforeMount
                                        LoadingComponent={RouteLoading}
                                    >
                                        {form}
                                    </PatientDetailsByStudyNumberProvider> :
                                    <InstitutionByCodeProvider 
                                        institutionCode={institutionCode ?? undefined}
                                        loadBeforeMount
                                        LoadingComponent={RouteLoading}
                                    >
                                        {form}
                                    </InstitutionByCodeProvider>
                        }
                    </Dialog>
                )
            }
        </>
    )
}

const ScreeningForm: FC<IScreeningFormProps> = ({ refreshScreeningForms }) => {
    //const classes = useStyles();
    const { data: patient } = useContext(PatientContext);
    const { data: formDefinition } = useContext(FormDefinitionContext);

    return (
        <>
            {
                patient && formDefinition && (
                    <PageLayout
                        breadcrumbs={<FormBreadcrumbs />}
                    >
                        <ScreeningCrfForm />
                    </PageLayout>
                )   
            }

            <ScreeningDialogForm refreshScreeningForms={refreshScreeningForms} />
        </>
    )
}

export default ScreeningForm;
